import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { ArrowRight, Copy, Link2, RefreshCw, Search, X } from 'lucide-react'
import type { CaseEndpoint } from '../../../types/case-container'

interface SuccessViewProps {
  count: number
  hostname: string
  onClose: () => void
  t: TFunction<'cases'>
}

export function SuccessView({ count, hostname, onClose, t }: SuccessViewProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
            <Copy className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('infrastructure.copyLinks.linksCopied')}
          </h3>
          <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">
            {t('infrastructure.copyLinks.successMessage', { count, hostname })}
          </p>
          <button
            onClick={onClose}
            className="rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700"
          >
            {t('actions.done')}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ModalHeaderProps {
  onClose: () => void
  t: TFunction<'cases'>
}

export function ModalHeader({ onClose, t }: ModalHeaderProps): ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fuchsia-100 dark:bg-fuchsia-950/50">
          <Copy className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('infrastructure.copyLinks.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('infrastructure.copyLinks.selectSource')}
          </p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface TargetIndicatorProps {
  hostname: string
  t: TFunction<'cases'>
}

export function TargetIndicator({ hostname, t }: TargetIndicatorProps): ReactElement {
  return (
    <div className="border-b border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-700 dark:bg-slate-800/50">
      <p className="mb-1 text-xs tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('infrastructure.copyLinks.copyingTo')}
      </p>
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-400" />
        <span className="font-medium text-slate-900 dark:text-slate-100">{hostname}</span>
      </div>
    </div>
  )
}

interface SearchBarProps {
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  t: TFunction<'cases'>
}

export function SearchBar({ searchTerm, setSearchTerm, t }: SearchBarProps): ReactElement {
  return (
    <div className="border-b border-slate-200 px-6 py-3 dark:border-slate-700">
      <div className="relative">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
          }}
          placeholder={t('infrastructure.searchEndpoints')}
          className="w-full rounded-md border border-slate-300 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        />
      </div>
    </div>
  )
}

export function ErrorBanner({ error }: { error: string }): ReactElement {
  return (
    <div className="border-b border-red-200 bg-red-50 px-6 py-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
    </div>
  )
}

interface EmptyStateProps {
  hasSearchTerm: boolean
  t: TFunction<'cases'>
}

function EmptyState({ hasSearchTerm, t }: EmptyStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Link2 className="mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
      <p className="text-slate-600 dark:text-slate-400">
        {hasSearchTerm
          ? t('infrastructure.copyLinks.noMatchingEndpoints')
          : t('infrastructure.copyLinks.noEndpointsWithLinks')}
      </p>
    </div>
  )
}

interface EndpointRowProps {
  endpoint: CaseEndpoint
  isSelected: boolean
  linkCount: number
  onSelect: (id: string) => void
}

function EndpointRow({
  endpoint,
  isSelected,
  linkCount,
  onSelect,
}: EndpointRowProps): ReactElement {
  const cardCls = isSelected
    ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/30'
    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
  return (
    <button
      onClick={() => {
        onSelect(endpoint.id)
      }}
      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${cardCls}`}
    >
      <SelectionDot isSelected={isSelected} />
      <EndpointMeta endpoint={endpoint} isSelected={isSelected} />
      <LinkCountBadge isSelected={isSelected} linkCount={linkCount} />
    </button>
  )
}

function SelectionDot({ isSelected }: { isSelected: boolean }): ReactElement {
  const cls = isSelected
    ? 'border-fuchsia-500 bg-fuchsia-500'
    : 'border-slate-300 dark:border-slate-600'
  return (
    <div className={`h-4 w-4 flex-shrink-0 rounded-full border-2 transition-colors ${cls}`}>
      {isSelected && (
        <div className="flex h-full w-full items-center justify-center">
          <div className="h-1.5 w-1.5 rounded-full bg-white" />
        </div>
      )}
    </div>
  )
}

interface EndpointMetaProps {
  endpoint: CaseEndpoint
  isSelected: boolean
}

function EndpointMeta({ endpoint, isSelected }: EndpointMetaProps): ReactElement {
  const nameCls = isSelected
    ? 'text-fuchsia-700 dark:text-fuchsia-400'
    : 'text-slate-900 dark:text-slate-100'
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className={`truncate font-medium ${nameCls}`}>{endpoint.hostname}</span>
        {endpoint.deviceType && (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {endpoint.deviceType.replaceAll('_', ' ')}
          </span>
        )}
      </div>
      {endpoint.ipAddresses && endpoint.ipAddresses.length > 0 && (
        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
          {endpoint.ipAddresses.join(', ')}
        </p>
      )}
    </div>
  )
}

interface LinkCountBadgeProps {
  isSelected: boolean
  linkCount: number
}

function LinkCountBadge({ isSelected, linkCount }: LinkCountBadgeProps): ReactElement {
  const cls = isSelected
    ? 'text-fuchsia-600 dark:text-fuchsia-400'
    : 'text-slate-600 dark:text-slate-400'
  return (
    <div className="flex flex-shrink-0 items-center gap-1 text-sm">
      <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
      <span className={`font-medium ${cls}`}>{linkCount}</span>
    </div>
  )
}

interface EndpointListProps {
  filteredEndpoints: CaseEndpoint[]
  selectedEndpointId: string | null
  endpointLinkCounts: Map<string, number>
  setSelectedEndpointId: React.Dispatch<React.SetStateAction<string | null>>
  searchTerm: string
  t: TFunction<'cases'>
}

export function EndpointList(props: EndpointListProps): ReactElement {
  const {
    filteredEndpoints,
    selectedEndpointId,
    endpointLinkCounts,
    setSelectedEndpointId,
    searchTerm,
    t,
  } = props
  if (filteredEndpoints.length === 0) {
    return (
      <div className="flex-1 overflow-auto px-6 py-3">
        <EmptyState hasSearchTerm={!!searchTerm} t={t} />
      </div>
    )
  }
  return (
    <div className="flex-1 overflow-auto px-6 py-3">
      <div className="space-y-2">
        {filteredEndpoints.map((ep) => (
          <EndpointRow
            key={ep.id}
            endpoint={ep}
            isSelected={selectedEndpointId === ep.id}
            linkCount={endpointLinkCounts.get(ep.id) ?? 0}
            onSelect={setSelectedEndpointId}
          />
        ))}
      </div>
    </div>
  )
}

interface FooterProps {
  filteredCount: number
  selectedLinkCount: number
  selectedEndpointId: string | null
  copying: boolean
  onClose: () => void
  onCopy: () => void
  t: TFunction<'cases'>
}

export function Footer(props: FooterProps): ReactElement {
  const { filteredCount, selectedLinkCount, selectedEndpointId, copying, onClose, onCopy, t } =
    props
  return (
    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('infrastructure.copyLinks.endpointCount', { count: filteredCount })}
      </p>
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {t('actions.cancel')}
        </button>
        <button
          onClick={onCopy}
          disabled={!selectedEndpointId || copying}
          className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {copying && <RefreshCw className="h-4 w-4 animate-spin" />}
          <Copy className="h-4 w-4" />
          {t('infrastructure.copyLinks.copyCount', { count: selectedLinkCount })}
        </button>
      </div>
    </div>
  )
}
